import React, { useState, useRef } from "react";
import { StepProps } from "antd";
import LandingLayout from "../Components/Layout/LandingLayout/LandingLayout";
import PostOnboardingLayout from "../Components/Layout/PostOnboardingLayout";
import GetStarted from "./GetStarted";
import BusinessType from "./BusinessType";
import BusinessIdentityVerification from "./BusinessIdentityVerification";
import BvnVerifiedDetails from "./BvnVerifiedDetails";
import RegisteredBusiness from "./RegisteredBusiness";
import Partnership from "./Partnership";
import NGO from "./NGO";
import LimitedLiability from "./LimitedLiability";
import StepIcon from "../Components/StepIcon";
import { BusinessTypes } from "../../Interfaces";
import styles from "./CorporateOnboardingModal.module.scss";
import { CheckOutlined } from "@ant-design/icons";
import { ICorporateCustomer } from "../hooks/useCorporateCustomers";

interface ICorporateOnboardingModal {
  isOpen: boolean;
  onClose: () => void;
  currentStep?: string;
  resumeCustomerData?: ICorporateCustomer;
}

const CorporateOnboardingModal: React.FC<ICorporateOnboardingModal> = ({
  isOpen,
  onClose,
  currentStep: initialStep = "business-type",
  resumeCustomerData,
}) => {
  const [currentStep, setCurrentStep] = useState("business-type");
  const [formData, setFormData] = useState<any>({});
  const [stepperItems, setStepperItems] = useState<StepProps[]>([]);
  const [postOnboardingCurrentStep, setPostOnboardingCurrentStep] = useState(0);

  // Generic ref to store custom back handlers for any nested step component
  const nestedStepBackHandlerRef = useRef<(() => void) | null>(null);

  // Initialize resume mode when resumeCustomerData is provided
  React.useEffect(() => {
    if (resumeCustomerData && isOpen) {
      console.log("Resuming onboarding with data:", resumeCustomerData);
      // Determine post-onboarding step based on business_type_id
      const businessTypeToStepMap: Record<number, string> = {
        [BusinessTypes.REGISTERED_BUSINESS]: "registered-business",
        [BusinessTypes.PARTNERSHIP]: "partnership",
        [BusinessTypes.NGO]: "ngo",
        [BusinessTypes.LIMITED_LIABILITY]: "limited-liability",
      };

      const resumeStep =
        businessTypeToStepMap[resumeCustomerData.business_subtype_id];

      if (resumeStep) {
        // Pre-populate form data with customer data
        setFormData({
          ...resumeCustomerData,
          customerSubType: resumeCustomerData.business_subtype_id,
        });

        // Initialize stepper for the business type
        initializeStepperForBusinessType(
          resumeCustomerData.business_subtype_id
        );

        // Set to post-onboarding step
        setCurrentStep(resumeStep);

        // Start from first sub-step (Business Information)
        setPostOnboardingCurrentStep(1);
      }
    }
  }, [resumeCustomerData, isOpen]);

  if (!isOpen) return null;

  // Define post-onboarding steps (these will use PostOnboardingLayout)
  const postOnboardingSteps = [
    "registered-business",
    "partnership",
    "ngo",
    "limited-liability",
  ];

  // Check if current step is a post-onboarding step
  const isPostOnboarding = postOnboardingSteps.includes(currentStep);

  // Initialize stepper items based on business type when entering post-onboarding
  const initializeStepperForBusinessType = (businessType: number) => {
    const steps: StepProps[] = [];

    if (businessType === BusinessTypes.REGISTERED_BUSINESS) {
      steps.push(
        {
          title: "Get Started",
          icon: (
            <StepIcon customClass="completed">
              <CheckOutlined style={{ fontSize: "14px", color: "#25ad25" }} />
            </StepIcon>
          ),
          status: "finish",
        },
        {
          title: "Business Information",
          icon: <StepIcon>1</StepIcon>,
        },
        {
          title: "Business Owner's Information",
          icon: <StepIcon>2</StepIcon>,
        },
        {
          title: "Business Documents",
          icon: <StepIcon>3</StepIcon>,
        },
        {
          title: "Complete Onboarding",
          icon: <StepIcon>4</StepIcon>,
        }
      );
    } else if (businessType === BusinessTypes.NGO) {
      steps.push(
        {
          title: "Get Started",
          icon: (
            <StepIcon customClass="completed">
              <CheckOutlined style={{ fontSize: "14px", color: "#25ad25" }} />
            </StepIcon>
          ),
          status: "finish",
        },
        {
          title: "Business Information",
          icon: <StepIcon>1</StepIcon>,
        },
        {
          title: "Registered Trustees",
          icon: <StepIcon>2</StepIcon>,
        },
        {
          title: "Business Documents",
          icon: <StepIcon>3</StepIcon>,
        },
        {
          title: "Complete Onboarding",
          icon: <StepIcon>4</StepIcon>,
        }
      );
    } else if (businessType === BusinessTypes.PARTNERSHIP) {
      steps.push(
        {
          title: "Get Started",
          icon: (
            <StepIcon customClass="completed">
              <CheckOutlined style={{ fontSize: "14px", color: "#25ad25" }} />
            </StepIcon>
          ),
          status: "finish",
        },
        {
          title: "Business Information",
          icon: <StepIcon>1</StepIcon>,
        },
        {
          title: "Partner",
          icon: <StepIcon>2</StepIcon>,
        },
        {
          title: "Business Documents",
          icon: <StepIcon>3</StepIcon>,
        },
        {
          title: "Complete Onboarding",
          icon: <StepIcon>4</StepIcon>,
        }
      );
    } else if (businessType === BusinessTypes.LIMITED_LIABILITY) {
      steps.push(
        {
          title: "Get Started",
          icon: (
            <StepIcon customClass="completed">
              <CheckOutlined style={{ fontSize: "14px", color: "#25ad25" }} />
            </StepIcon>
          ),
          status: "finish",
        },
        {
          title: "Business Information",
          icon: <StepIcon>1</StepIcon>,
        },
        {
          title: "Directors",
          icon: <StepIcon>2</StepIcon>,
        },
        {
          title: "Business Documents",
          icon: <StepIcon>3</StepIcon>,
        },
        {
          title: "Complete Onboarding",
          icon: <StepIcon>4</StepIcon>,
        }
      );
    }

    setStepperItems(steps);
  };

  const handleContinue = (data: Record<string, any>, nextStep: string) => {
    setFormData((prev: any) => ({ ...prev, ...data }));
    console.log("Form data updated:", data);

    console.log(
      "postOnboardingSteps:",
      postOnboardingSteps,
      "nextStep:",
      nextStep
    );

    // If transitioning to post-onboarding, initialize stepper
    if (postOnboardingSteps.includes(nextStep) && data.customerSubType) {
      initializeStepperForBusinessType(Number(data.customerSubType));
    }

    setCurrentStep(nextStep);
  };

  const handleBack = (previousStep: string) => {
    setCurrentStep(previousStep);
  };

  const handleClose = () => {
    // Reset to first step and clear form data when modal is closed
    setCurrentStep("business-type");
    setFormData({});
    setPostOnboardingCurrentStep(0);
    setStepperItems([]);
    onClose();
  };

  const handleStepChange = (step: number) => {
    // Allow user to click stepper to navigate to different steps
    setPostOnboardingCurrentStep(step);
  };

  const getBackStep = () => {
    // Map current step to previous step
    const stepMap: Record<string, string | null> = {
      "business-type": null, // First step, no back
      "bvn-verification": "business-type",
      "bvn-verified-details": "bvn-verification",
      "get-started": "bvn-verified-details",
      "registered-business": "get-started",
      partnership: "get-started",
      ngo: "get-started",
      "limited-liability": "get-started",
    };
    return stepMap[currentStep] || null;
  };

  const handleBackNavigation = () => {
    const previousStep = getBackStep();
    if (previousStep) {
      handleBack(previousStep);
    }
  };

  // Generic handler to register custom back handlers from nested step components
  const registerNestedBackHandler = (handler: (() => void) | null) => {
    nestedStepBackHandlerRef.current = handler;
  };

  // Define which steps have nested sub-steps that need custom back navigation
  const stepsWithNestedNavigation = [
    "get-started",
    "registered-business",
    "partnership",
    "ngo",
    "limited-liability",
  ]; // Add more step keys here as needed

  // Determine which back handler to use based on current step
  const getBackHandler = () => {
    // If current step has nested navigation and has provided a custom handler, use it
    if (
      stepsWithNestedNavigation.includes(currentStep) &&
      nestedStepBackHandlerRef.current
    ) {
      return nestedStepBackHandlerRef.current;
    }
    // Otherwise use the default parent-level navigation
    return handleBackNavigation;
  };

  const renderStep = () => {
    switch (currentStep) {
      case "business-type":
        return (
          <BusinessType
            onContinue={(businessType) =>
              handleContinue(
                { business_type: businessType },
                "bvn-verification"
              )
            }
            defaultValue={formData.business_type}
          />
        );
      case "bvn-verification":
        return (
          <BusinessIdentityVerification
            onContinue={(data) => handleContinue(data, "bvn-verified-details")}
            defaultValue={formData.bvn}
          />
        );
      case "bvn-verified-details":
        return (
          <BvnVerifiedDetails
            onContinue={(data) => handleContinue(data, "get-started")}
            defaultValues={{
              first_name: formData.firstname,
              last_name: formData.lastname,
              gender: formData.gender,
              dob: formData.dob,
              country: "NG",
            }}
          />
        );
      case "get-started":
        return (
          <GetStarted
            onContinue={(data) => {
              // Store the response data and determine next step based on business type
              const updatedData = { ...formData, ...data };
              setFormData(updatedData);

              // Route based on business type
              const businessType = Number(updatedData.customerSubType);
              let nextStep = "registered-business"; // default

              if (businessType === BusinessTypes.REGISTERED_BUSINESS) {
                nextStep = "registered-business";
              } else if (businessType === BusinessTypes.PARTNERSHIP) {
                nextStep = "partnership";
              } else if (businessType === BusinessTypes.NGO) {
                nextStep = "ngo";
              } else if (businessType === BusinessTypes.LIMITED_LIABILITY) {
                nextStep = "limited-liability";
              }

              // Initialize stepper for the business type
              initializeStepperForBusinessType(businessType);

              // Set initial step to 1 (Business Information) since "Get Started" is step 0
              setPostOnboardingCurrentStep(1);

              setCurrentStep(nextStep);
            }}
            onBack={handleBackNavigation}
            defaultValues={formData}
            onBackHandlerReady={registerNestedBackHandler}
          />
        );
      case "registered-business":
        return (
          <RegisteredBusiness
            resumeCustomerData={resumeCustomerData}
            onComplete={() => {
              // Onboarding completed, close modal or navigate
              handleClose();
            }}
            onBack={handleBackNavigation}
            onBackHandlerReady={registerNestedBackHandler}
            formData={formData}
            onStepChange={setPostOnboardingCurrentStep}
            navigateToStep={postOnboardingCurrentStep}
          />
        );
      case "partnership":
        return (
          <Partnership
            resumeCustomerData={resumeCustomerData}
            onComplete={() => {
              // Onboarding completed, close modal or navigate
              handleClose();
            }}
            onBack={handleBackNavigation}
            onBackHandlerReady={registerNestedBackHandler}
            formData={formData}
            onStepChange={setPostOnboardingCurrentStep}
            navigateToStep={postOnboardingCurrentStep}
          />
        );
      case "ngo":
        return (
          <NGO
            resumeCustomerData={resumeCustomerData}
            onComplete={() => {
              // Onboarding completed, close modal or navigate
              handleClose();
            }}
            onBack={handleBackNavigation}
            onBackHandlerReady={registerNestedBackHandler}
            formData={formData}
            onStepChange={setPostOnboardingCurrentStep}
            navigateToStep={postOnboardingCurrentStep}
          />
        );
      case "limited-liability":
        return (
          <LimitedLiability
            resumeCustomerData={resumeCustomerData}
            onComplete={() => {
              // Onboarding completed, close modal or navigate
              handleClose();
            }}
            onBack={handleBackNavigation}
            onBackHandlerReady={registerNestedBackHandler}
            formData={formData}
            onStepChange={setPostOnboardingCurrentStep}
            navigateToStep={postOnboardingCurrentStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {isPostOnboarding ? (
          // Use PostOnboardingLayout for post-onboarding steps
          <PostOnboardingLayout
            onClose={handleClose}
            onBack={getBackHandler()}
            showBackButton={getBackStep() !== null}
            stepperList={stepperItems}
            currentStep={postOnboardingCurrentStep}
            handleChange={handleStepChange}
          >
            <div className={styles.onboardingContainer}>{renderStep()}</div>
          </PostOnboardingLayout>
        ) : (
          // Use LandingLayout for pre-onboarding steps
          <LandingLayout
            onClose={handleClose}
            onBack={getBackHandler()}
            showBackButton={getBackStep() !== null}
            showCarousel={true}
            currentStep={currentStep}
          >
            <div className={styles.onboardingContainer}>{renderStep()}</div>
          </LandingLayout>
        )}
      </div>
    </div>
  );
};

export default CorporateOnboardingModal;
